import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Grid,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Stack,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useTheme } from "@mui/material/styles";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from "recharts";
import {
  format,
  subDays,
  startOfDay,
  endOfDay,
  parseISO,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { Link } from "react-router-dom";
import StarIcon from "@mui/icons-material/Star";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import PeopleIcon from "@mui/icons-material/People";
import PercentIcon from "@mui/icons-material/Percent";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import { ColorModeContext } from "../../context/ColorMode/ColorModeContext";
import toastError from "../../errors/toastError";

const PERIODS = {
  today: 0,
  week: 7,
  month: 30,
  quarter: 90,
};

const useStyles = makeStyles(theme => ({
  content: {
    flex: 1,
    overflowY: "auto",
    ...theme.scrollbarStyles,
    paddingBottom: theme.spacing(3),
  },
  filterBar: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    alignItems: "center",
    justifyContent: "space-between",
  },
  statCard: {
    padding: theme.spacing(2.5),
    height: "100%",
  },
  statValue: {
    fontWeight: 700,
    fontSize: "1.85rem",
    lineHeight: 1.1,
  },
  statLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  chartPaper: {
    padding: theme.spacing(2),
    height: 340,
    display: "flex",
    flexDirection: "column",
  },
  chartTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
  },
  chartBody: {
    flex: 1,
    minHeight: 0,
  },
  tablePaper: {
    padding: theme.spacing(2),
  },
}));

const toInputDate = date => format(date, "yyyy-MM-dd");

const formatDayLabel = day => {
  try {
    return format(parseISO(day), "dd/MM", { locale: ptBR });
  } catch {
    return day;
  }
};

const formatDateTime = value => {
  try {
    return format(new Date(value), "dd/MM/yyyy HH:mm", { locale: ptBR });
  } catch {
    return "";
  }
};

const StatCard = ({ label, value, color, icon }) => {
  const classes = useStyles();
  return (
    <Paper variant="outlined" className={classes.statCard}>
      <Typography className={classes.statLabel}>
        {icon}
        {label}
      </Typography>
      <Typography className={classes.statValue} style={{ color }}>
        {value}
      </Typography>
    </Paper>
  );
};

const PesquisaDashboard = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { mode } = useContext(ColorModeContext);

  const [periodKey, setPeriodKey] = useState("month");
  const [dateFrom, setDateFrom] = useState(toInputDate(subDays(new Date(), 30)));
  const [dateTo, setDateTo] = useState(toInputDate(new Date()));
  const [operatorName, setOperatorName] = useState("");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const chartTextColor = theme.palette.text.secondary;
  const gridColor =
    mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
  const tooltipStyle = {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    color: theme.palette.text.primary,
  };

  const applyPreset = useCallback(key => {
    const today = new Date();
    const days = PERIODS[key] ?? 30;
    const from =
      key === "today" ? startOfDay(today) : startOfDay(subDays(today, days));
    setPeriodKey(key);
    setDateFrom(toInputDate(from));
    setDateTo(toInputDate(endOfDay(today)));
  }, []);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const { data: response } = await api.get("/pesquisaDashboard", {
        params: {
          dateFrom,
          dateTo,
          name: operatorName.trim() || undefined,
        },
      });
      setData(response);
    } catch (err) {
      toastError(err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, operatorName]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const responsePieData = useMemo(() => {
    if (!data?.summary) return [];
    return [
      {
        name: "Respondidas",
        value: data.summary.responded,
        color: "#22c55e",
      },
      {
        name: "Não respondidas",
        value: data.summary.notResponded,
        color: "#e23b3b",
      },
    ].filter(item => item.value > 0);
  }, [data]);

  const handlePeriodChange = (_, value) => {
    if (value) applyPreset(value);
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Pesquisa de Satisfação</Title>
      </MainHeader>

      <Box className={classes.content}>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Avaliação dos atendimentos e desempenho dos operadores com base nas
          notas registradas nos tickets.
        </Typography>

        <Paper variant="outlined" className={classes.filterBar}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={periodKey}
            onChange={handlePeriodChange}
          >
            <ToggleButton value="today">Hoje</ToggleButton>
            <ToggleButton value="week">7 dias</ToggleButton>
            <ToggleButton value="month">30 dias</ToggleButton>
            <ToggleButton value="quarter">90 dias</ToggleButton>
          </ToggleButtonGroup>

          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
            <TextField
              label="Operador"
              size="small"
              value={operatorName}
              onChange={e => {
                setPeriodKey("custom");
                setOperatorName(e.target.value);
              }}
              placeholder="Filtrar por nome"
              sx={{ minWidth: 180 }}
            />
            <TextField
              label="De"
              type="date"
              size="small"
              value={dateFrom}
              onChange={e => {
                setPeriodKey("custom");
                setDateFrom(e.target.value);
              }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Até"
              type="date"
              size="small"
              value={dateTo}
              onChange={e => {
                setPeriodKey("custom");
                setDateTo(e.target.value);
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </Paper>

        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                  md: "repeat(3, 1fr)",
                  lg: "repeat(5, 1fr)",
                },
                gap: 2,
                mb: 2,
              }}
            >
              <StatCard
                label="Total pesquisas"
                value={data?.summary?.total ?? 0}
                color={theme.palette.primary.main}
                icon={<PeopleIcon fontSize="small" />}
              />
              <StatCard
                label="Respondidas"
                value={data?.summary?.responded ?? 0}
                color="#22c55e"
                icon={<ThumbUpIcon fontSize="small" />}
              />
              <StatCard
                label="Não respondidas"
                value={data?.summary?.notResponded ?? 0}
                color="#e23b3b"
                icon={<ThumbDownIcon fontSize="small" />}
              />
              <StatCard
                label="Média geral"
                value={data?.summary?.avgScore ?? 0}
                color="#eab308"
                icon={<StarIcon fontSize="small" />}
              />
              <StatCard
                label="Taxa de resposta"
                value={`${data?.summary?.responseRate ?? 0}%`}
                color="#3b82f6"
                icon={<PercentIcon fontSize="small" />}
              />
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" className={classes.chartPaper}>
                  <Typography variant="subtitle1" className={classes.chartTitle}>
                    Taxa de resposta
                  </Typography>
                  <Box className={classes.chartBody}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={responsePieData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={85}
                          paddingAngle={2}
                        >
                          {responsePieData.map(entry => (
                            <Cell key={entry.name} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={tooltipStyle} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={8}>
                <Paper variant="outlined" className={classes.chartPaper}>
                  <Typography variant="subtitle1" className={classes.chartTitle}>
                    Distribuição de notas
                  </Typography>
                  <Box className={classes.chartBody}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data?.scoreDistribution ?? []}>
                        <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
                        <XAxis
                          dataKey="score"
                          stroke={chartTextColor}
                          label={{ value: "Nota", position: "insideBottom", offset: -2 }}
                        />
                        <YAxis allowDecimals={false} stroke={chartTextColor} />
                        <Tooltip contentStyle={tooltipStyle} />
                        <Bar
                          dataKey="count"
                          name="Quantidade"
                          fill={theme.palette.primary.main}
                          radius={[6, 6, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper variant="outlined" className={classes.chartPaper}>
                  <Typography variant="subtitle1" className={classes.chartTitle}>
                    Média por atendente
                  </Typography>
                  <Box className={classes.chartBody}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data?.byAttendant ?? []} layout="vertical" margin={{ left: 12 }}>
                        <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
                        <XAxis type="number" domain={[0, "auto"]} stroke={chartTextColor} />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={110}
                          stroke={chartTextColor}
                          fontSize={11}
                        />
                        <Tooltip contentStyle={tooltipStyle} />
                        <Bar
                          dataKey="avgScore"
                          name="Média"
                          fill="#eab308"
                          radius={[0, 6, 6, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper variant="outlined" className={classes.chartPaper}>
                  <Typography variant="subtitle1" className={classes.chartTitle}>
                    Pesquisas por atendente
                  </Typography>
                  <Box className={classes.chartBody}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data?.byAttendant ?? []}>
                        <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
                        <XAxis
                          dataKey="name"
                          stroke={chartTextColor}
                          fontSize={11}
                          interval={0}
                          angle={-18}
                          textAnchor="end"
                          height={55}
                        />
                        <YAxis allowDecimals={false} stroke={chartTextColor} />
                        <Tooltip contentStyle={tooltipStyle} />
                        <Legend />
                        <Bar
                          dataKey="responded"
                          name="Respondidas"
                          stackId="a"
                          fill="#22c55e"
                        />
                        <Bar
                          dataKey="notResponded"
                          name="Não respondidas"
                          stackId="a"
                          fill="#e23b3b"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper variant="outlined" className={classes.chartPaper}>
                  <Typography variant="subtitle1" className={classes.chartTitle}>
                    Evolução da média de notas
                  </Typography>
                  <Box className={classes.chartBody}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={data?.byDay ?? []}>
                        <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
                        <XAxis
                          dataKey="day"
                          tickFormatter={formatDayLabel}
                          stroke={chartTextColor}
                        />
                        <YAxis stroke={chartTextColor} domain={[0, "auto"]} />
                        <Tooltip
                          contentStyle={tooltipStyle}
                          labelFormatter={formatDayLabel}
                        />
                        <Line
                          type="monotone"
                          dataKey="avgScore"
                          name="Média"
                          stroke={theme.palette.primary.main}
                          strokeWidth={2}
                          dot={{ r: 3 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper variant="outlined" className={classes.tablePaper}>
                  <Typography variant="subtitle1" className={classes.chartTitle}>
                    Detalhamento das avaliações
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Atendente</TableCell>
                          <TableCell>Contato</TableCell>
                          <TableCell align="center">Ticket</TableCell>
                          <TableCell align="center">Data</TableCell>
                          <TableCell align="center">Faixa</TableCell>
                          <TableCell align="center">Nota</TableCell>
                          <TableCell align="center">Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(data?.rows ?? []).length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center">
                              Nenhuma pesquisa no período selecionado.
                            </TableCell>
                          </TableRow>
                        ) : (
                          data.rows.map(row => (
                            <TableRow key={row.id}>
                              <TableCell>{row.nome}</TableCell>
                              <TableCell>{row.numero}</TableCell>
                              <TableCell align="center">
                                <Link to={`/tickets/${row.id}`}>{row.id}</Link>
                              </TableCell>
                              <TableCell align="center">
                                {formatDateTime(row.updatedAt)}
                              </TableCell>
                              <TableCell align="center">
                                {row.rangeNote || "—"}
                              </TableCell>
                              <TableCell align="center">
                                {row.responded ? (
                                  <Chip
                                    size="small"
                                    label={row.note}
                                    color="success"
                                    variant="outlined"
                                  />
                                ) : (
                                  <Chip
                                    size="small"
                                    label="Sem nota"
                                    color="warning"
                                    variant="outlined"
                                  />
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  size="small"
                                  label={row.status}
                                  variant="outlined"
                                />
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </MainContainer>
  );
};

export default PesquisaDashboard;
